/**
 * Sinalização de vencimento.
 *
 * Requisito do usuário: a proposta **fica acessível para sempre**, mas sinaliza claramente que as
 * condições comerciais não valem mais após a validade. No legado a proposta ficava inacessível.
 *
 * Estratégia de renderização, e por que ela é assim:
 *
 * As três variantes da faixa são emitidas no HTML, e apenas a correspondente ao estado no momento
 * da publicação fica visível. Um script pequeno reavalia no carregamento e troca a visível, se o
 * estado mudou desde então.
 *
 * A alternativa — emitir só a variante atual e reescrever o texto por JavaScript — exigiria duplicar
 * toda a copy dentro de strings de script, o que dobraria a manutenção do texto comercial. E a
 * alternativa oposta — não emitir nada e injetar tudo por script — deixaria o documento sem
 * sinalização alguma quando o JavaScript não roda, inclusive no PDF salvo.
 *
 * Com a abordagem adotada: sem JavaScript, o leitor vê o estado da data da publicação, e a data de
 * validade continua impressa no herói e no rodapé. Com JavaScript, a faixa está sempre correta.
 */

import {
  calcularVigencia,
  descreverVigencia,
  type DocumentoProposta,
  type EstadoVigencia,
} from '@emaster/dominio';

import { atributo, codificarUrl, escaparHtml } from './escapar.ts';
import { dataCurta } from './componentes.ts';
import { icone } from './tokens.ts';

/** Estilo de cada estado. `vigente` não exibe faixa: nada a avisar. */
const ESTILO: Readonly<
  Record<Exclude<EstadoVigencia, 'vigente'>, { fundo: string; borda: string; icone: string }>
> = {
  vence_em_breve: {
    fundo: 'bg-amber-50 text-amber-900',
    borda: 'border-amber-400',
    icone: 'clock',
  },
  vencida: {
    fundo: 'bg-red-50 text-red-900',
    borda: 'border-red-500',
    icone: 'alert-triangle',
  },
};

/** Data de hoje em `AAAA-MM-DD`, em UTC. */
function hojeUtc(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * CTA de revalidação.
 *
 * Transforma o vencimento em gancho comercial em vez de beco sem saída: o cliente que abre uma
 * proposta vencida tem um caminho de um clique para pedir a atualizada.
 */
function ctaRevalidacao(doc: DocumentoProposta): string {
  const assunto = codificarUrl(`Proposta ${doc.numero} - solicitar atualizacao`);
  const corpo = codificarUrl(
    `Olá, ${doc.responsavel.nomeExibicao}.\n\n` +
      `Gostaria de solicitar uma versão atualizada da proposta ${doc.numero} ` +
      `(${doc.projeto}), cuja validade era ${dataCurta(doc.validaAte)}.\n\n` +
      `Obrigado.`,
  );

  const mail = `mailto:${doc.responsavel.email}?subject=${assunto}&body=${corpo}`;
  const wa = `https://wa.me/${doc.responsavel.whatsapp}?text=${assunto}`;

  return `<span class="flex flex-wrap items-center gap-2 mt-2 sm:mt-0 sm:ml-auto shrink-0">
                    <a href=${atributo(mail)} class="inline-flex items-center gap-2 bg-brand-blue hover:bg-brand-light text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors">
                        ${icone('mail', 'w-4 h-4')} Solicitar proposta atualizada
                    </a>
                    <a href=${atributo(wa)} target="_blank" rel="noopener" class="inline-flex items-center gap-2 bg-green-600 hover:bg-green-500 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors">
                        ${icone('message-circle', 'w-4 h-4')} WhatsApp
                    </a>
                </span>`;
}

/** Uma variante da faixa. `oculta` controla o estado inicial. */
function variante(
  estado: Exclude<EstadoVigencia, 'vigente'>,
  doc: DocumentoProposta,
  oculta: boolean,
): string {
  const e = ESTILO[estado];
  const validade = dataCurta(doc.validaAte);

  const titulo =
    estado === 'vencida'
      ? `Esta proposta venceu em ${validade}.`
      : `Esta proposta vence em ${validade}.`;

  const detalhe =
    estado === 'vencida'
      ? 'As condições comerciais e os valores apresentados não são mais válidos. O conteúdo permanece disponível para consulta e referência.'
      : 'Após essa data, as condições comerciais e os valores apresentados deixam de ser válidos.';

  return `<div data-vencimento=${atributo(estado)}${oculta ? ' hidden' : ''} class="${e.fundo} border-b-4 ${e.borda}">
            <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex flex-col sm:flex-row sm:items-center gap-2">
                <span class="flex items-start gap-3">
                    <span class="shrink-0 mt-0.5">${icone(e.icone, 'w-5 h-5')}</span>
                    <span class="text-sm leading-relaxed">
                        <strong class="font-bold">${escaparHtml(titulo)}</strong>
                        <span class="block">${escaparHtml(detalhe)}</span>
                    </span>
                </span>
                ${ctaRevalidacao(doc)}
            </div>
        </div>`;
}

/**
 * Faixa de vencimento, para ser inserida imediatamente após a abertura do `<body>`.
 *
 * Deliberadamente **sem** a classe `no-print`: o aviso precisa aparecer no documento impresso e no
 * PDF salvo. Uma proposta vencida impressa sem aviso é pior que a proposta na tela.
 *
 * @param hoje Data de referência em `AAAA-MM-DD`. Injetável para teste determinístico.
 */
export function faixaVencimento(doc: DocumentoProposta, hoje: string = hojeUtc()): string {
  const { estado } = calcularVigencia(doc.validaAte, hoje);

  return `
    <div id="faixa-vencimento" data-valida-ate=${atributo(doc.validaAte)}>
        ${variante('vence_em_breve', doc, estado !== 'vence_em_breve')}
        ${variante('vencida', doc, estado !== 'vencida')}
    </div>`;
}

/**
 * Reforço acima da tabela de investimento.
 *
 * A faixa do topo pode ter saído da área visível quando o cliente chega aos números. Este bloco
 * garante que a informação apareça no exato momento em que ela muda a leitura dos valores.
 */
export function avisoInvestimento(doc: DocumentoProposta, hoje: string = hojeUtc()): string {
  const { estado } = calcularVigencia(doc.validaAte, hoje);
  const validade = dataCurta(doc.validaAte);

  return `<div data-aviso-investimento="vencida"${estado === 'vencida' ? '' : ' hidden'} class="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-xl mb-8">
                <div class="flex items-start gap-4">
                    <span class="shrink-0 text-red-500">${icone('alert-triangle', 'w-6 h-6')}</span>
                    <div>
                        <h4 class="font-bold text-red-900 mb-1">Valores fora de validade</h4>
                        <p class="text-sm text-red-900/80 leading-relaxed">
                            Os valores desta seção foram calculados para a proposta ${escaparHtml(doc.numero)}, válida até ${escaparHtml(validade)}. Câmbio, preços da AWS e condições de crédito mudam com o tempo. Solicite uma versão atualizada antes de tomar qualquer decisão com base nestes números.
                        </p>
                    </div>
                </div>
            </div>`;
}

/**
 * Script que reavalia o estado no carregamento.
 *
 * Mantido mínimo e sem dependência: compara a data de validade com o relógio do visitante e troca a
 * variante visível. Comparar no cliente é adequado aqui porque isto é **sinalização comercial, não
 * controle de acesso** — o requisito é justamente que o conteúdo permaneça acessível. Manipular o
 * relógio local para esconder o aviso não daria ao visitante nada que ele já não tenha.
 */
export function scriptVencimento(): string {
  return `
        (function () {
            var raiz = document.getElementById('faixa-vencimento');
            if (!raiz) return;

            var validaAte = raiz.getAttribute('data-valida-ate');
            if (!validaAte) return;

            var partes = validaAte.split('-');
            var limite = Date.UTC(+partes[0], +partes[1] - 1, +partes[2]);

            var agora = new Date();
            var hoje = Date.UTC(agora.getFullYear(), agora.getMonth(), agora.getDate());

            var dias = Math.round((limite - hoje) / 86400000);
            var estado = dias < 0 ? 'vencida' : dias <= ${String(7)} ? 'vence_em_breve' : 'vigente';

            raiz.querySelectorAll('[data-vencimento]').forEach(function (el) {
                el.hidden = el.getAttribute('data-vencimento') !== estado;
            });

            document.querySelectorAll('[data-aviso-investimento]').forEach(function (el) {
                el.hidden = el.getAttribute('data-aviso-investimento') !== estado;
            });
        })();`;
}

/** Descrição textual do estado, para uso no painel. Reexportado por conveniência. */
export function descricaoVigencia(doc: DocumentoProposta, hoje: string = hojeUtc()): string {
  return descreverVigencia(calcularVigencia(doc.validaAte, hoje));
}
